import React, { useEffect, useRef, useState } from "react";
import TableHead from "@/components/tables/TableHead";
import UsersTable from "@/components/tables/UsersTable";
import { fetchAllGroups } from "@/services/group.service";
import { fetchAllRoles } from "@/services/role.service";
import { fetchUsers } from "@/services/user.service";
import { options } from "@/utils/constants/constants";
import { useDataQueries } from "@/utils/hooks/useDataQueries";
import { useWindowInfiniteScroll } from "@/utils/hooks/useWindowInfiniteScroll";
import { getListCacheBootstrap, writeListCache } from "@/utils/listPageCache";

const isEmptyFilter = (value: Record<string, unknown>) =>
    Object.keys(value).length === 0;

const UserList = () => {
    const [
        pageNo,
        setPageNo,
        dataLength,
        setDataLength,
        sorting,
        setSorting,
        filter,
        setFilter,
        searchItem,
        setSearchItem,
        resetDataQueries,
    ] = useDataQueries();

    const [appliedFilter, setAppliedFilter] = useState(filter);

    const listBootstrap = getListCacheBootstrap("users", {
        dataLength,
        sorting,
        searchItem,
        filter: appliedFilter,
    });

    const [isLoading, setIsLoading] = useState(!listBootstrap.hasCache);
    const [users, setUsers] = useState<any>(listBootstrap.items);
    const [usersLeft, setUsersLeft] = useState(true);
    const [usersHistory, setUsersHistory] = useState<any>(listBootstrap.items);
    const [allFetched, setAllFetched] = useState(false);
    const [groups, setGroups] = React.useState([]);
    const [roles, setRoles] = React.useState([]);
    const [dataLoad, setDataLoad] = useState(!listBootstrap.hasCache);
    const [totalCount, setTotalCount] = useState(listBootstrap.totalCount);
    const loadMoreLockRef = useRef(false);

    useEffect(() => {
        fetchAllRoles(
            (response: any) => setRoles(response.data.roles),
            (error: any) => console.log(error)
        );
        fetchAllGroups(
            (response: any) => setGroups(response.data.groups),
            (error: any) => console.log(error)
        );
    }, []);

    useEffect(() => {
        const { cacheKey, cached } = getListCacheBootstrap("users", {
            dataLength,
            sorting,
            searchItem,
            filter: appliedFilter,
        });

        setPageNo(1);
        setUsersLeft(true);
        setAllFetched(false);

        if (cached && isEmptyFilter(appliedFilter)) {
            setUsers(cached.items);
            setUsersHistory(cached.items);
            setTotalCount(cached.totalCount);
            setDataLoad(false);
            setIsLoading(false);
            return;
        }

        setUsers([]);
        setUsersHistory([]);
        setIsLoading(true);
        setDataLoad(true);

        fetchUsers(
            1,
            dataLength,
            sorting,
            appliedFilter,
            searchItem,
            (response: any) => {
                const nextUsers = response.data.users ?? [];

                writeListCache(
                    cacheKey,
                    nextUsers,
                    response.data.total_count
                );

                setTotalCount(response.data.total_count);
                setUsers(nextUsers);
                setUsersHistory(nextUsers);

                if (nextUsers.length < dataLength) {
                    setUsersLeft(false);
                    setAllFetched(true);
                } else {
                    setUsersLeft(true);
                    setAllFetched(false);
                }

                setIsLoading(false);
                setDataLoad(false);
                loadMoreLockRef.current = false;
            },
            (error: any) => {
                console.log(error);
                setIsLoading(false);
                setDataLoad(false);
                loadMoreLockRef.current = false;
            }
        );
    }, [dataLength, sorting, searchItem, appliedFilter]);

    useWindowInfiniteScroll({
        enabled: !allFetched && !isLoading && usersLeft,
        onLoadMore: () => {
            if (loadMoreLockRef.current) {
                return;
            }
            loadMoreLockRef.current = true;

            const nextPage = Number(pageNo) + 1;
            setPageNo(nextPage);
            setIsLoading(true);

            fetchUsers(
                nextPage,
                dataLength,
                sorting,
                appliedFilter,
                searchItem,
                (response: any) => {
                    const nextUsers = response.data.users ?? [];
                    const reportedTotal = response.data.total_count;

                    setIsLoading(false);
                    loadMoreLockRef.current = false;
                    setTotalCount(reportedTotal);

                    setUsers((prevUsers: any[]) => {
                        const updatedUsers = [...prevUsers, ...nextUsers];
                        const hasMore =
                            nextUsers.length > 0 &&
                            (reportedTotal != null
                                ? updatedUsers.length < reportedTotal
                                : nextUsers.length >= dataLength);
                        setUsersLeft(hasMore);
                        setAllFetched(!hasMore);
                        setUsersHistory(updatedUsers);
                        return updatedUsers;
                    });
                },
                (error: any) => {
                    console.log(error);
                    setIsLoading(false);
                    loadMoreLockRef.current = false;
                }
            );
        },
    });

    const reset = () => {
        resetDataQueries?.();
        setAppliedFilter({});
        setPageNo(1);
        setUsersLeft(true);
        setAllFetched(false);
        setUsers([]);
        setUsersHistory([]);
        setIsLoading(true);
        setDataLoad(true);
    };

    const search = (searchText: string) => {
        setSearchItem(searchText);
    };

    const handleApplyFilter = (filterOverride?: any) => {
        const nextFilter = filterOverride ?? filter;

        setPageNo(1);
        setUsersLeft(true);
        setAllFetched(false);
        setUsers([]);
        setUsersHistory([]);
        setDataLoad(true);
        setIsLoading(true);
        setAppliedFilter(nextFilter);
    };

    return (
        <div className="gs-list-page-card">
            <div className="gs-list-page-card-header">
                <TableHead
                    id="user-table"
                    handleReset={reset}
                    pageNo={pageNo as number}
                    dataLength={dataLength as number}
                    groups={groups}
                    roles={roles}
                    setFilter={setFilter}
                    filter={filter}
                    setDataLength={setDataLength as (dataLength: number) => void}
                    reset={reset}
                    search={search}
                    title="Users"
                    options={options}
                    handleApplyFilter={handleApplyFilter}
                    setPageNo={setPageNo}
                    currentEnvo={users.length}
                    totalEnvo={totalCount}
                    searchItem={searchItem as string}
                />
            </div>

            <div>
                <UsersTable
                    is_allow_manage_shared_access={true}
                    id="user-table"
                    reset={reset}
                    setUsers={setUsers}
                    search={search}
                    isLoading={isLoading}
                    dataLength={dataLength as number}
                    sorting={sorting}
                    setSorting={setSorting as (sorting: any) => void}
                    title="Users"
                    searchItem={searchItem as string}
                    allFetched={allFetched}
                    users={users}
                    setPageNo={setPageNo}
                    dataload={dataLoad}
                    resetDataQueries={resetDataQueries}
                />
            </div>
        </div>
    );
};

export default UserList;
